import {requirePermission} from '@/modules/auth/permissions'
import {useGetInfiniteBasicDataItems} from '@/modules/basic-data/api'
import {BasicDataIndexPageHeader, BasicDataTable} from '@/modules/basic-data/components'
import {openBasicDataDialog} from '@/modules/basic-data/components/basic-data-dialog/util'
import {isValidResource} from '@/modules/basic-data/functions'
import {BasicDataSearchSchema} from '@/modules/basic-data/schemas'
import {BasicDataResources} from '@cypro/engine/schema'
import {createFileRoute, notFound} from '@tanstack/react-router'
import {useTranslate} from '@tolgee/react'
import {
  LuArmchair,
  LuBiohazard,
  LuBox,
  LuBoxes,
  LuBriefcaseBusiness,
  LuContainer,
  LuCuboid,
  LuDroplets,
  LuFileText,
  LuPill,
  LuRoute,
  LuShield,
  LuSyringe,
  LuUserRound,
} from 'react-icons/lu'

export const Route = createFileRoute('/configuration/basic-data/$resource')({
  component: BasicDataResourcePage,
  validateSearch: BasicDataSearchSchema,
  beforeLoad: ({params}) => {
    requirePermission('basic_data.manage')()
    if (!isValidResource(params.resource)) throw notFound()
  },
})

const LIMIT = 50

const ResourceToEmptyStateMap = {
  [BasicDataResources.adminRoutes]: {
    title: 'no_administration_routes',
    description: 'create_new_administration_route_to_get_started',
    icon: LuRoute,
  },
  [BasicDataResources.applicationForms]: {
    title: 'no_application_forms',
    description: 'create_new_application_form_to_get_started',
    icon: LuSyringe,
  },
  [BasicDataResources.containerMaterials]: {
    title: 'no_container_materials',
    description: 'create_new_container_material_to_get_started',
    icon: LuCuboid,
  },
  [BasicDataResources.containerTypes]: {
    title: 'no_container_types',
    description: 'create_new_container_type_to_get_started',
    icon: LuBoxes,
  },
  [BasicDataResources.diagnoses]: {
    title: 'no_diagnoses',
    description: 'create_new_diagnosis_to_get_started',
    icon: LuFileText,
  },
  [BasicDataResources.hazardClasses]: {
    title: 'no_hazard_classes',
    description: 'create_new_hazard_class_to_get_started',
    icon: LuBiohazard,
  },
  [BasicDataResources.solutionTypes]: {
    title: 'no_solution_types',
    description: 'create_new_solution_type_to_get_started',
    icon: LuDroplets,
  },
  [BasicDataResources.medicineForms]: {
    title: 'no_medicine_forms',
    description: 'create_new_medicine_form_to_get_started',
    icon: LuPill,
  },
  [BasicDataResources.cancerTypes]: {
    title: 'no_cancer_types',
    description: 'create_new_cancer_type_to_get_started',
    icon: LuPill,
  },
  [BasicDataResources.companies]: {
    title: 'no_companies',
    description: 'create_new_company_to_get_started',
    icon: LuBriefcaseBusiness,
  },
  [BasicDataResources.stockLocations]: {
    title: 'no_stock_locations',
    description: 'create_new_stock_location_to_get_started',
    icon: LuContainer,
  },
  [BasicDataResources.treatmentChairs]: {
    title: 'no_treatment_chairs',
    description: 'create_new_treatment_chair_to_get_started',
    icon: LuArmchair,
  },
  [BasicDataResources.storageLabels]: {
    title: 'no_storage_labels',
    description: 'create_new_storage_label_to_get_started',
    icon: LuBox,
  },
  [BasicDataResources.insuranceCompanies]: {
    title: 'no_insurance_companies',
    description: 'create_new_insurance_company_to_get_started',
    icon: LuShield,
  },
  [BasicDataResources.generalPhysicians]: {
    title: 'no_general_physicians',
    description: 'create_new_general_physician_to_get_started',
    icon: LuUserRound,
  },
} as const

function BasicDataResourcePage() {
  const {t} = useTranslate('common')
  const {resource} = Route.useParams()
  const search = Route.useSearch()
  const [sort] = search?.sort || []

  if (!isValidResource(resource)) return null

  const infinite = useGetInfiniteBasicDataItems({
    resource,
    limit: LIMIT,
    filters: {...search?.filters, status: []},
    sort: {
      field: sort?.id,
      dir: sort?.desc ? 'desc' : 'asc',
    },
  })

  const {getPageQuery, getSingleQuery, toStartRow, listContextParams} = infinite
  const emptyState = ResourceToEmptyStateMap[resource]

  return (
    <>
      <BasicDataIndexPageHeader item={{id: undefined, resource}} />

      <BasicDataTable
        resource={resource}
        virtualizer={{getPageQuery, getSingleQuery, toStartRow, listContextParams}}
        table={{state: {sorting: search.sort}}}
        emptyState={{
          ...emptyState,
          title: t(emptyState.title),
          description: t(emptyState.description),
        }}
        onRowClick={item => openBasicDataDialog({...item, resource})}
      />
    </>
  )
}
